import { compact } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { sortBy } from "lodash"
import { flatten, head, isNil, prop, reject } from "ramda"
import { createSelector } from "reselect"
import type {
    CategoriesListResponse,
    SchematizedData,
    SchematizedPage,
    SchematizedPageGroup,
    SchematizedSize,
} from "../../../../graphql/types"
import { CategoryType, SchematizedTileType } from "../../../../graphql/types"
import { appendAssetsHostnameToUrl } from "../../../../utils/environment"
import { pipeline } from "../../../../utils/functional"
import { getSelectedLocationId } from "../../../common/locationsPicker/selectors"
import { areAccountsAndLocationsLoading, isStateLoading } from "../../../filters/selectors"
import type { StateObject } from "../../../redux/types"
import { getCurrentPath, getRecordEditId } from "../../../router/selectors"
import { getNewCategoryColor, getNewCategoryIcon } from "../../categories/itemsCategories/selectors"
import { getRootCategory } from "../../categories/itemsCategories/table/selectors"
import { getItemsAndCategoriesListResponse } from "../../categories/selectors"
import { computeOverriddenValue, formatCategoryPriceRange } from "../../categories/utils"
import type { SchematizedState } from "./reducer"
import type { SchematizedTileComponentItem } from "./types"
import { countCategoriesInSchematizedGroups, getLinkedGridId, projectStickyTiles } from "./utils"

export const getSchematizedStateObject = (state: StateObject): SchematizedState => state.schematized

export const getSchematizedLayouts = (state: StateObject): SchematizedData[] =>
    getSchematizedStateObject(state).schematizedLayouts.sort((a, b) => a.sortIndex - b.sortIndex)

export const getSchematizedSelectedLayoutData = (state: StateObject): Maybe<SchematizedData> => {
    const layoutId = getSchematizedSelectedLayoutId(state)
    return layoutId ? getSchematizedLayouts(state).find(l => l.layoutId === layoutId) : null
}

export const getSchematizedSelectedLayoutId = (state: StateObject): Maybe<string> => {
    const layoutId = getSchematizedStateObject(state).selectedLayoutId

    if (!layoutId) {
        const firstLayout = head(getSchematizedLayouts(state))
        return firstLayout ? firstLayout.layoutId : null
    }

    return layoutId
}

export const getCurrentSchematizedGroups = (state: StateObject): SchematizedPageGroup[] => {
    const layoutId = getSchematizedSelectedLayoutId(state)
    const layout = layoutId ? getSchematizedLayouts(state).find(l => l.layoutId === layoutId) : null
    return layout ? layout.data : []
}

export const getEditSchematizedCustomFolderId = (state: StateObject) => {
    const isInSchematized = getCurrentPath(state)?.query?.tab === "schematized"
    return isInSchematized && getRecordEditId(state)
}

export const getSchematizedSearchKey = (state: StateObject): Maybe<string> => getSchematizedStateObject(state).searchKey

export const getEditSchematizedCustomFolder = (state: StateObject) => {
    const data = getSchematizedComponentData(state)
    const customFolderTile = data.find(t => t.id === getEditSchematizedCustomFolderId(state))
    return customFolderTile ?? null
}

export const getSchematizedSelectedGridId = (state: StateObject): Maybe<string> => {
    const selectedGridId = getSchematizedStateObject(state).selectedGridId

    if (!selectedGridId) {
        const firstRootPage = head(
            compact(getCurrentSchematizedGroups(state).map(g => (g.isRoot ? head(g.pages) : null)))
        )
        return firstRootPage ? firstRootPage.id : null
    }

    return selectedGridId
}

export const getSchematizedSelectedGridGroup = (state: StateObject): Maybe<SchematizedPageGroup> => {
    const selectedGridId = getSchematizedSelectedGridId(state)
    const data = getCurrentSchematizedGroups(state)
    return data.find(g => g.pages.some(p => p.id === selectedGridId))
}

export const getSchematizedSelectedGroupPages = (state: StateObject): SchematizedPage[] => {
    const selectedGroup = getSchematizedSelectedGridGroup(state)
    return selectedGroup ? selectedGroup.pages : []
}

export const isSelectedGridInRoot = (state: StateObject): boolean => {
    const selectedGridId = getSchematizedSelectedGridId(state)
    const data = getCurrentSchematizedGroups(state)
    return data.some(g => g.isRoot && g.pages.find(p => p.id === selectedGridId))
}

export const getSelectedSchematizedResolution = (state: StateObject): SchematizedSize =>
    getSchematizedStateObject(state).size

export const getResolutionChanged = (state: StateObject): boolean => getSchematizedStateObject(state).sizeChanged

export const getSchematizedComponentData = (state: StateObject): SchematizedTileComponentItem[] => {
    const data = projectStickyTiles(getCurrentSchematizedGroups(state))
    const itemsAndCats = getItemsAndCategoriesListResponse(state)?.data ?? []
    const categoriesTiles = getAllProductAndFolderTiles(state)
    const currentGridId = getSchematizedSelectedGridId(state)
    // @ts-expect-error
    const currentGrid: Maybe<SchematizedPage> = pipeline(
        currentGridId
            ? data.map(group => group.pages.find(grid => grid.id === currentGridId))
            : data.map(group => (group.isRoot ? head(group.pages) : null)),
        flatten,
        reject(isNil),
        head
    )
    return currentGrid
        ? currentGrid.tiles
              .filter(t => {
                  if (t.categoryId && itemsAndCats.length > 0 && !itemsAndCats.find(c => c.id === t.categoryId)) {
                      return false
                  }
                  return true
              })
              .map(t => {
                  const categoryData = t.categoryId ? categoriesTiles.find(c => c.categoryId === t.categoryId) : null

                  const { name, icon, color } = t.customFolder ? t.customFolder : categoryData || {}
                  return {
                      id: t.id,
                      x: t.x,
                      y: t.y,
                      name: name,
                      color: color,
                      icon: icon,
                      isActive: categoryData ? categoryData.isActive : !!t.customFolder,
                      type: categoryData && categoryData.type,
                      price: categoryData && categoryData.price,
                      categoryId: t.categoryId,
                      gridId: currentGrid.id,
                      linkedGridId: getLinkedGridId(t, currentGrid, data),
                      targetPageGroupId: t.targetPageGroupId,
                      isBackButton: t.type === SchematizedTileType.BackButton,
                      isSticky: t.isSticky || false,
                      tileImageUrl: t.customFolder ? null : categoryData?.tileImageUrl,
                  }
              })
        : []
}

export const getAllProductAndFolderTiles: (arg0: StateObject) => SchematizedTileComponentItem[] = createSelector(
    getCurrentSchematizedGroups,
    getSelectedLocationId,
    getItemsAndCategoriesListResponse,
    (
        schematizedData: SchematizedPageGroup[],
        selectedLocationId: Maybe<string>,
        itemsAndCats: Maybe<CategoriesListResponse>
    ) => {
        if (!itemsAndCats) {
            return []
        }

        const itemsAndCategories: CategoriesListResponse = itemsAndCats

        const result = itemsAndCategories.data.map(c => {
            const isActiveOverriddenValue = computeOverriddenValue(
                itemsAndCategories.overrides,
                selectedLocationId,
                c.id,
                prop("isActive")
            )
            const priceOverriddenValue = computeOverriddenValue(
                itemsAndCategories.overrides,
                selectedLocationId,
                c.id,
                prop("price")
            )
            return {
                id: null,
                x: null,
                y: null,
                name: c.displayName || c.name,
                type: c.type,
                icon: getNewCategoryIcon(c.id, itemsAndCategories.data),
                color: getNewCategoryColor(c.id, itemsAndCategories.data),
                parentId: c.parentId,
                price:
                    c.type !== CategoryType.Category && priceOverriddenValue != null
                        ? // @ts-expect-error
                          formatCategoryPriceRange(priceOverriddenValue, c.unit)
                        : formatCategoryPriceRange(c.price, c.unit),
                isActive: isActiveOverriddenValue != null ? isActiveOverriddenValue : c.isActive,
                categoryId: c.id,
                linkedGridId: null,
                gridId: null,
                isBackButton: false,
                isSticky: false,
                count: c.id ? countCategoriesInSchematizedGroups(c.id, schematizedData) : null,
                tileImageUrl: c.displayImagePOS ? appendAssetsHostnameToUrl(c.tileImagePath ?? null) : null,
            }
        })

        return sortBy(result, "name")
    }
)

export const getProductAndFolderTilesToAdd = (state: StateObject): SchematizedTileComponentItem[] => {
    const allTiles = getAllProductAndFolderTiles(state)
    const productAndCategories = getItemsAndCategoriesListResponse(state)
    const searchKey = getSchematizedSearchKey(state)
    return searchKey
        ? allTiles.filter(t => {
              if (t.name && t.name.toLowerCase().includes(searchKey.toLowerCase())) {
                  return t
              }

              const tileCategory = ((productAndCategories && productAndCategories.data) || []).find(
                  k => k.id === t.parentId
              )

              if (
                  tileCategory &&
                  tileCategory.name &&
                  tileCategory.name.toLowerCase().includes(searchKey.toLowerCase())
              ) {
                  return t
              }

              return false
          })
        : allTiles
}

export const isPlaceOccupied = (
    schematizedUiData: SchematizedTileComponentItem[],
    x: number,
    y: number,
    gridId: Maybe<string>
): boolean => {
    return schematizedUiData.some(tile => tile.gridId === gridId && tile.x === x && tile.y === y)
}

export const didSchematizedDataChanged = (state: StateObject) => {
    const schematizedState = getSchematizedStateObject(state)
    return (
        schematizedState.oldSchematizedLayouts.length > 0 &&
        schematizedState.schematizedLayouts.length > 0 &&
        schematizedState.oldSchematizedLayouts !== schematizedState.schematizedLayouts
    )
}

export const isAddPageBtnActive = (state: StateObject): boolean => {
    const currentGroup = getSchematizedSelectedGridGroup(state)
    return currentGroup ? currentGroup.pages.length < 5 : true
}

export const isRemovePageBtnActive = (state: StateObject): boolean => {
    const currentGroup = getSchematizedSelectedGridGroup(state)
    return currentGroup ? currentGroup.pages.length > 1 : false
}

export const hasMultipleSchematizedTabs = (state: StateObject): boolean => {
    return getSchematizedLayouts(state).length > 1
}

export const isSchematizedAddTabBtnEnabled = (state: StateObject): boolean => {
    return getSchematizedLayouts(state).length < 5
}

export const getSchematizedRenameLayoutValue = (state: StateObject): Maybe<string> => {
    return getSchematizedStateObject(state).layoutRenameInputValue
}

export const isSchematizedLayoutRenamingActive = (state: StateObject): boolean => {
    return getSchematizedStateObject(state).layoutRenameInputValue !== null
}

export const isSchematizedLayoutRenamingSaveDisabled = (state: StateObject): boolean => {
    const value = getSchematizedStateObject(state).layoutRenameInputValue
    return value == null || value === ""
}

export const shouldShowSchematizedLoading = (state: StateObject): boolean =>
    getSchematizedLayouts(state).length < 1 ||
    !getRootCategory(state) ||
    isStateLoading(state, "schematized-view") ||
    areAccountsAndLocationsLoading(state)

export const isSaveButtonActive = (state: StateObject): boolean =>
    !!((didSchematizedDataChanged(state) || getResolutionChanged(state)) && !shouldShowSchematizedLoading(state))
